import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/vue-query';
import { tryOnBeforeUnmount, tryOnMounted } from '@vueuse/core';
import type { MaybeRefOrGetter } from 'vue';
import { computed, ref, toValue, unref, watch, watchEffect } from 'vue';

import { RowCountCache } from '#public/commons-global';
import type { DbOrderJsRuntime } from '#public_ts/api';
import type { ProbablyString } from '#public_ts/commons/fe-commons';

import type { ComponentState } from './component-state';

export type SimpleGetDataPager = {
    // zero based
    firstRow: number;
    maxRows: number;
};

export type SimpleGetDataFilter<K extends string> = {
    filter?: ProbablyString;
    order?: DbOrderJsRuntime<K>[];
};

export type SimpleGetDataResponse<T, REQ = unknown, RESP = unknown> = {
    data: T[];
    query?: REQ;
    response?: RESP;
};

export type FetchDataFn<
    K extends string,
    T,
    F extends SimpleGetDataFilter<K> = SimpleGetDataFilter<K>,
    R extends SimpleGetDataResponse<T> = SimpleGetDataResponse<T>,
> = (
    filter: F,
    pager: SimpleGetDataPager,
    options: { rowCountCache: InstanceType<typeof RowCountCache> },
) => Promise<R>;

export function useQueryClientHelper<
    K extends string,
    T,
    F extends SimpleGetDataFilter<K> = SimpleGetDataFilter<K>,
    R extends SimpleGetDataResponse<T> = SimpleGetDataResponse<T>,
>(
    fetchData: FetchDataFn<K, T, F, R>,
    currentFiler: MaybeRefOrGetter<F> | F,
    options: {
        cacheKey?: string | symbol;
        initialPager?: SimpleGetDataPager;
        queryKey?: () => unknown[];
    } = {},
) {
    const cacheKey = options.cacheKey ?? Symbol('useQueryClientHelper');
    const filter = ref<F>(toValue(currentFiler));
    const pager = ref<SimpleGetDataPager>(options.initialPager ?? { firstRow: 0, maxRows: 10 });
    const rowCountCache = new RowCountCache();
    const defaultQueryOptions = {
        staleTime: 60000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    };

    // zero based
    const currentPage = computed(() => {
        const _pager = unref(pager);
        return Math.floor(_pager.firstRow / _pager.maxRows);
    });
    const maxPages = computed(() => {
        const _pager = unref(pager);
        const _rowCount = rowCountCache.rowCount;
        const _pageSize = _pager.maxRows;
        const _maxPages = (_rowCount < 0) ? -1 : Math.ceil(_rowCount / _pageSize);
        const _limitPages = Math.ceil(1000 / _pageSize);
        return (_maxPages < 0) ? _limitPages : Math.min(_limitPages, _maxPages);
    });
    const maxRows = computed(() => unref(pager).maxRows);

    watch(() => toValue(currentFiler), (v) => {
        filter.value = v;
    });

    const commonData = {
        filter,
        pager,
        currentPage,
        maxPages,
    };

    function createMobileQuery(state?: ComponentState) {
        const queryKey: (() => unknown[]) = () => [cacheKey, filter, maxRows, ...(options.queryKey?.() ?? [])];
        const mobileQuery = useInfiniteQuery({
            ...defaultQueryOptions,
            queryKey: queryKey(),
            initialPageParam: currentPage,
            queryFn: ({ pageParam }) => {
                const _pager = unref(pager);

                const _pager0 = {
                    firstRow: pageParam * _pager.maxRows,
                    maxRows: _pager.maxRows,
                };
                return fetchData(unref(filter), _pager0, { rowCountCache });
            },
            getNextPageParam: (lastPage, allPages, lastPageParam) => {
                if(lastPage.data.length < unref(maxRows)) {
                    return undefined;
                }
                if((lastPageParam + 1) >= unref(maxPages)) {
                    return undefined;
                }
                return lastPageParam + 1;
            },
            getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
                if(firstPageParam <= 0) {
                    return undefined;
                }
                return firstPageParam - 1;
            },
            maxPages,
            placeholderData: keepPreviousData,
            enabled: () => !unref(state?.frozen),
        });

        watchEffect(() => {
            const _pager = unref(pager);
            _pager.firstRow = (unref(mobileQuery.data)?.pages.length ?? 0) * _pager.maxRows;
        });

        const queryClient = useQueryClient();

        watch(filter, () => {
            invalidateQuery(true);
        }, { deep: true });

        function invalidateQuery(paging?: boolean) {
            if(paging) {
                queryClient.setQueryData(queryKey(), () => ({
                    pages: [],
                    pageParams: [0],
                }));
            }
            queryClient.invalidateQueries({ queryKey: queryKey() });
        }

        let offUpdate: (() => void) | undefined;
        tryOnMounted(() => {
            offUpdate = state?.onUpdate(() => {
                mobileQuery.refetch();
            })?.off;
        });
        tryOnBeforeUnmount(() => {
            offUpdate?.();
        });

        return {
            ...commonData,
            mobileQuery,
            invalidateQuery,
        };
    }

    function createDesktopQuery(state?: ComponentState) {
        const queryKey: (() => unknown[]) = () => [cacheKey, filter, pager, ...(options.queryKey?.() ?? [])];
        const queryFn = () => {
            const _pager = unref(pager);
            return fetchData(unref(filter), _pager, { rowCountCache });
        };
        const desktopQuery = useQuery({
            ...defaultQueryOptions,
            queryKey: queryKey(),
            queryFn,
            maxPages,
            placeholderData: keepPreviousData,
            enabled: () => !unref(state?.frozen),
        });

        const queryClient = useQueryClient();

        watch(filter, () => {
            invalidateQuery(true);
        }, { deep: true });

        function invalidateQuery(paging?: boolean) {
            if(paging) {
                // will invalidate because of query key
                unref(pager).firstRow = 0;
            } else {
                queryClient.invalidateQueries({ queryKey: queryKey() });
            }
        }

        let offUpdate: (() => void) | undefined;
        tryOnMounted(() => {
            offUpdate = state?.onUpdate(() => {
                desktopQuery.refetch();
            })?.off;
        });
        tryOnBeforeUnmount(() => {
            offUpdate?.();
        });

        return {
            ...commonData,
            desktopQuery,
            invalidateQuery,
        };
    }

    function createDesktopQueryClient(state?: ComponentState) {
        const queryKey: (() => unknown[]) = () => [cacheKey, filter, pager, ...(options.queryKey?.() ?? [])];
        const queryFn = () => {
            const _pager = unref(pager);
            return fetchData(unref(filter), _pager, { rowCountCache });
        };

        const queryClient = useQueryClient();

        function invalidateQuery(paging?: boolean) {
            if(paging) {
                // will invalidate because of query key
                unref(pager).firstRow = 0;
            } else {
                queryClient.invalidateQueries({ queryKey: queryKey() });
            }
        }

        function fetchQuery(_filter: F, _pager: SimpleGetDataPager) {
            filter.value = _filter;
            pager.value = _pager;
            return queryClient.fetchQuery({
                ...defaultQueryOptions,
                queryKey: queryKey(),
                queryFn,
            });
        }

        let offUpdate: (() => void) | undefined;
        tryOnMounted(() => {
            offUpdate = state?.onUpdate(() => {
                // invalidate current page, changes are usually made here
                invalidateQuery();
            })?.off;
        });
        tryOnBeforeUnmount(() => {
            offUpdate?.();
        });

        return {
            ...commonData,
            invalidateQuery,
            fetchQuery,
        };
    }

    return {
        ...commonData,
        createMobileQuery,
        createDesktopQuery,
        createDesktopQueryClient,
    };
}
